import { EmissionFactorService, TypeOfUse } from '../../../../@core/utils/emission-factor.service';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Observable, Subscription, combineLatest } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { KPI } from '../../../../@core/api';
import { TranslateModule } from '@ngx-translate/core';
import { CorporatePathwaysComponent } from '../corporate-pathways/corporate-pathways.component';
import { AsyncPipe, DecimalPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NbSelectModule, NbOptionModule } from '@nebular/theme';
import { PerimeterEnergyTrajectoryService, PerimeterPathwayService, PerimetersService, UsersService } from '../../../../@core/utils';
import * as R from 'ramda';
import { GROUP_PERMISSION_EDIT_ENERGY_SETTINGS } from '../../../../@core/api/models';

interface ToUPathway {
  name: string,
  typeOfUse: TypeOfUse,
  emission: number[],
  consumption: number[]
}

@Component({
  selector: 'app-energy-carbon-objectives',
  styleUrls: ['./energy-carbon-objectives.component.scss'],
  templateUrl: './energy-carbon-objectives.component.html',
  standalone: true,
  imports: [
    NbSelectModule,
    FormsModule,
    NbOptionModule,
    CorporatePathwaysComponent,
    DecimalPipe,
    TranslateModule,
    AsyncPipe,
  ],
})

export class EnergyCarbonObjectivesComponent implements OnInit, OnDestroy {

  @Input() public objectiveType: 'energy' | 'carbon';

  public selectedProviderId: number;
  public seletecTypeOfUseByProvider: { [provider: number]: TypeOfUse } = {};
  public startYear = new Date().getFullYear() - 3;
  public emissionFactorProviders: KPI.EmissionFactorProvider[] = [];
  public ToUPathway: ToUPathway[] = [];
  public years: number[] = R.range(this.startYear, 2051);
  private perimeters: KPI.Perimeter[] = []
  private subscriptions: Subscription[] = [];
  public selectedToUPathway: KPI.SelectedToUPathway | null = null;
  public canEdit$: Observable<boolean> = this.usersService.hasCurrentUserPermissions([GROUP_PERMISSION_EDIT_ENERGY_SETTINGS]);
  public isSiteObjectiveDisabled$: Observable<boolean> = this.perimeterEnergyTrajectoryService.selectedTrajectoryBuildingIds$.pipe(
    map(buildingIds => buildingIds.length !== 1),
    distinctUntilChanged(),
  );

  constructor(
    private emissionFactorService: EmissionFactorService,
    private pathwayService: PerimeterPathwayService,
    private perimetersService: PerimetersService,
    private perimeterEnergyTrajectoryService: PerimeterEnergyTrajectoryService,
    private usersService: UsersService,
  ) { }

  private energyProviderId: number;
  private carbonProviderId: number;

  public ngOnInit(): void {

    this.subscriptions.push(
      combineLatest([
        this.emissionFactorService.emissionFactorProviders$,
        this.emissionFactorService.getSelectedProvider$('energy'),
        this.emissionFactorService.getSelectedProvider$('carbon'),
        this.perimetersService.perimeters$,
        this.pathwayService.getSelectedPathway()
      ]).subscribe(([emissionFactorProviders, energyProvider, carbonProvider, perimeters, selectedPathway]) => {
        this.selectedProviderId = this.objectiveType === 'energy' ? energyProvider.id : carbonProvider.id;
        this.energyProviderId = energyProvider.id;
        this.carbonProviderId = carbonProvider.id;
        this.perimeters = perimeters;
        this.selectedToUPathway = selectedPathway;
        this.emissionFactorProviders = this.sortProviders(emissionFactorProviders);
        this.getPathways();
      }),
    );

    this.subscriptions.push(
      this.perimeterEnergyTrajectoryService.selectedTrajectoryBuildingIds$.subscribe((buildingIds) => {
        const isSiteObjectiveEligible = buildingIds.length === 1;
        if (!isSiteObjectiveEligible && this.selectedProviderId === -1) {
          this.providerChanged(0);
        }
      }),
    );
  }

  public providerChanged(providerId: number): void {
    this.emissionFactorService.setSelectedProvider(this.objectiveType, providerId);
    this.selectedProviderId = providerId;
    if (this.objectiveType === 'energy') {
      this.energyProviderId = providerId;
    } else {
      this.carbonProviderId = providerId;
    }

    if (providerId === 0) {
      this.emissionFactorService.setTypeOfUseByProvider(this.objectiveType, providerId, 'OFF');
      return;
    }

    const selectedProvider = this.emissionFactorProviders.find(provider => provider.id === providerId);
    if (selectedProvider) {
      const selectedToU = this.ToUPathway.find(pathway => pathway.name === this.selectedToUPathway?.name);
      if (selectedToU) {
        this.selectToU(selectedToU);
      }
    }
  }

  public selectToUByName(name: string) {
    const pathway = this.ToUPathway.find(p => p.name === name);
    if (pathway) {
      this.selectToU(pathway);
    }
  }

  private getPathways(): void {
    this.subscriptions.push(
      this.pathwayService.requestPathway$(this.perimeters, this.years).subscribe((pathway) => {
        this.ToUPathway = [];
        pathway.provider_objectives.country_objectives.map((countryObj) => {
          const countryName = countryObj.country.name;
          const typeOfUses = countryObj.type_of_use_objectives;

          typeOfUses.map(typeOfUseObjectives => {
            const objectives = typeOfUseObjectives.objectives;

            const emissions = this.years.map(year => {
              const obj = objectives.find(o => o.year === year);
              return obj ? obj.emission : null;
            });

            const consumptions = this.years.map(year => {
              const obj = objectives.find(o => o.year === year);
              return obj ? obj.consumption : null;
            });

            this.ToUPathway.push({
              name: `${countryName} - ${typeOfUseObjectives.type_of_use_label}`,
              typeOfUse: typeOfUseObjectives.type_of_use,
              emission: emissions,
              consumption: consumptions
            });
          });
        });
      }),
    );
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  private sortProviders(providers: KPI.EmissionFactorProvider[]): KPI.EmissionFactorProvider[]{
    return providers.sort((a, b) => {
      if (!a.id) {
          return -1;
      } else if (!b.id) {
          return 1;
      } else {
          return 0;
      }
    });
  }

  public selectToU(selectedPathway: ToUPathway) {
    const targetYears = [2030, 2040, 2050];
    const yearIndices = targetYears.map(year => this.years.indexOf(year));
    const emission = {
      2030: selectedPathway.emission[yearIndices[0]],
      2040: selectedPathway.emission[yearIndices[1]],
      2050: selectedPathway.emission[yearIndices[2]]
    };

    const consumption = {
      2030: selectedPathway.consumption[yearIndices[0]],
      2040: selectedPathway.consumption[yearIndices[1]],
      2050: selectedPathway.consumption[yearIndices[2]]
    };

    const result = {
      name: selectedPathway.name,
      emission,
      consumption
    };

    this.pathwayService.setSelectedPathway(result);
    this.emissionFactorService.setTypeOfUseByProvider('energy', this.energyProviderId, selectedPathway.typeOfUse);
    this.emissionFactorService.setTypeOfUseByProvider('carbon', this.carbonProviderId, selectedPathway.typeOfUse);
  }
}
